import { useEffect, useRef, useState } from "react";
import { Route, Routes, useLocation } from "react-router-dom";

import RequireAuth from "./RequireAuth";
import { UserProvider } from "@/context/UserContext";
import { EmployeesProvider } from "@/context/EmployeesContext";
import { SocketContext } from "@/context/SocketContext";
import { useSocket } from "@/hooks/useSocket";
import { useSocketNotifications } from "@/hooks/useSocketNotifications";
import { useFavicon } from "@/hooks/useFavicon";

import { MapScreenshotRenderer } from "./MapScreenshotRenderer";

import { getConfig } from "@/api/core";
import {
  getUnreadCount,
  noticesChangedEventName,
  noticesChangedStorageKey,
} from "@/api/notices";

// Layouts
import AuthLayout from "@/layouts/AuthLayout";
import PassportLayout from "@/layouts/PassportLayout";

// Routes
import MainRoutes from "@/routes/MainRoutes";
import AdminRoutes from "@/routes/AdminRoutes";

// Pages
import Login from "@/pages/Login";
import RequestForm from "@/pages/RequestForm/_id";
import RequestEntry from "@/pages/RequestEntry/_id";
import ObjectPassport from "@/pages/ObjectPassport/_id";

import LoadingScreen from "@/components/LoadingScreen";

const DEFAULT_TITLE = "Teamtools";
const FALLBACK_FAVICON = "data:,";

// Компонент для инициализации сокет-уведомлений внутри провайдеров
const SocketNotificationsHandler = ({
  socket,
  setRedDot,
  setUnreadCount,
  refreshUnreadCount,
}) => {
  useSocketNotifications(socket, setRedDot, setUnreadCount, refreshUnreadCount);

  return null;
};

function App() {
  const location = useLocation();
  const { socket, connected, reconnect } = useSocket();

  const [config, setConfig] = useState(null);
  const [redDot, setRedDot] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const refreshSeqRef = useRef(0);

  const refreshUnreadCount = async () => {
    const seq = ++refreshSeqRef.current;
    try {
      const nextCount = await getUnreadCount();
      if (seq !== refreshSeqRef.current) {
        return;
      }
      setUnreadCount(nextCount);
      setRedDot(nextCount > 0);
    } catch (error) {
      console.error("Ошибка обновления счетчика непрочитанных:", error);
    }
  };

  // Используем кастомные хуки

  useFavicon(unreadCount, config?.favicon_path);

  useEffect(() => {
    let isMounted = true;

    const loadConfig = async () => {
      try {
        const loadedConfig = await getConfig();

        if (!isMounted) return;

        setConfig(loadedConfig);

        // Устанавливаем заголовок страницы
        document.title = loadedConfig.browser_tab_title || DEFAULT_TITLE;

        // Устанавливаем favicon
        const existingLink = document.querySelector("link[rel*='icon']");
        const link = existingLink || document.createElement("link");

        link.rel = "icon";
        link.href = loadedConfig.favicon_path || FALLBACK_FAVICON;

        if (!existingLink) {
          document.head.appendChild(link);
        }
      } catch (error) {
        console.error("Ошибка загрузки конфигурации:", error);

        if (!isMounted) return;

        // Устанавливаем значения по умолчанию при ошибке
        document.title = DEFAULT_TITLE;
        setConfig({
          browser_tab_title: DEFAULT_TITLE,
          favicon_path: FALLBACK_FAVICON,
        });
      }
    };

    loadConfig();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    refreshUnreadCount();
    const handleNoticesChanged = () => {
      refreshUnreadCount();
    };
    const handleStorageSync = (event) => {
      if (event.key === noticesChangedStorageKey) {
        refreshUnreadCount();
      }
    };
    window.addEventListener(noticesChangedEventName, handleNoticesChanged);
    window.addEventListener("storage", handleStorageSync);

    // Fallback для межбраузерной синхронизации, если сокет-событие не дошло.
    const syncInterval = setInterval(() => {
      refreshUnreadCount();
    }, 15000);

    const handleFocusSync = () => {
      refreshUnreadCount();
    };
    window.addEventListener("focus", handleFocusSync);
    document.addEventListener("visibilitychange", handleFocusSync);
    window.addEventListener("online", handleFocusSync);

    return () => {
      window.removeEventListener(noticesChangedEventName, handleNoticesChanged);
      window.removeEventListener("storage", handleStorageSync);
      clearInterval(syncInterval);
      window.removeEventListener("focus", handleFocusSync);
      document.removeEventListener("visibilitychange", handleFocusSync);
      window.removeEventListener("online", handleFocusSync);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (location.pathname === "/login") {
      setUnreadCount(0);
      setRedDot(false);
    }
  }, [location.pathname]);

  // Показываем загрузку пока конфиг не загружен
  if (config === null) {
    return <LoadingScreen />;
  }

  return (
    <EmployeesProvider>
      <UserProvider>
        <SocketContext.Provider value={{ socket, connected, reconnect }}>
          <SocketNotificationsHandler
            socket={socket}
            setRedDot={setRedDot}
            setUnreadCount={setUnreadCount}
            refreshUnreadCount={refreshUnreadCount}
          />

          <MapScreenshotRenderer />

          <Routes>
            {/* Публичные маршруты */}
            <Route
              path="/login"
              element={
                <AuthLayout config={config}>
                  <Login />
                </AuthLayout>
              }
            />

            {/* Маршрут для страниц заявок */}
            <Route path="/request-form/:uuid/:id" element={<RequestForm />} />
            <Route
              path="/request-entry/:uuid"
              element={<RequestEntry isPublic />}
            />

            {/* Маршрут для паспорта объекта */}
            <Route
              path="/passport/:id"
              element={
                <PassportLayout>
                  <ObjectPassport config={config} />
                </PassportLayout>
              }
            />

            {/* Защищенные маршруты - администраторские */}
            <Route
              path="/administration/*"
              element={
                <RequireAuth requireAdmin>
                  <AdminRoutes config={config} redDot={redDot} />
                </RequireAuth>
              }
            />

            {/* Защищенные маршруты - основные */}
            <Route
              path="*"
              element={
                <RequireAuth>
                  <MainRoutes config={config} redDot={redDot} />
                </RequireAuth>
              }
            />
          </Routes>
        </SocketContext.Provider>
      </UserProvider>
    </EmployeesProvider>
  );
}

export default App;
